import { createPublicClient, getAddress, http, type Hex } from "viem";
import { robinhoodChain } from "./chain";
import { walletClientFor } from "./metamask";

/** The Merkle distributor deployed from contracts/. */
export const distributorAbi = [
  {
    type: "function",
    name: "claim",
    stateMutability: "nonpayable",
    inputs: [
      { name: "index", type: "uint256" },
      { name: "account", type: "address" },
      { name: "amount", type: "uint256" },
      { name: "merkleProof", type: "bytes32[]" },
    ],
    outputs: [],
  },
  {
    type: "function",
    name: "isClaimed",
    stateMutability: "view",
    inputs: [{ name: "index", type: "uint256" }],
    outputs: [{ name: "", type: "bool" }],
  },
] as const;

export interface ClaimArgs {
  distributor: string;
  index: number;
  account: string;
  amount: string;
  proof: string[];
  chainId: number;
  rpcUrl: string;
  explorerUrl: string;
}

/** Sends claim(index, account, amount, proof) and hands back the transaction hash. */
export async function sendClaim(args: ClaimArgs): Promise<Hex> {
  const client = walletClientFor(args.account, args.chainId, args.rpcUrl, args.explorerUrl);
  return client.writeContract({
    address: getAddress(args.distributor) as Hex,
    abi: distributorAbi,
    functionName: "claim",
    args: [BigInt(args.index), getAddress(args.account) as Hex, BigInt(args.amount), args.proof as Hex[]],
  });
}

/**
 * Reads isClaimed over the public RPC. Hands back null when the read fails so the
 * UI can stay quiet rather than assert something it does not know.
 */
export async function readIsClaimed(
  distributor: string,
  index: number,
  chainId: number,
  rpcUrl: string,
  explorerUrl: string,
): Promise<boolean | null> {
  try {
    const client = createPublicClient({
      chain: robinhoodChain(rpcUrl, explorerUrl, chainId),
      transport: http(rpcUrl),
    });
    return await client.readContract({
      address: getAddress(distributor) as Hex,
      abi: distributorAbi,
      functionName: "isClaimed",
      args: [BigInt(index)],
    });
  } catch {
    return null;
  }
}
